import { useState, useContext } from "react";
import { useNavigate } from "@tanstack/react-router";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
import { useFinaliseOrderSave } from "../../hooks/useFinaliseOrderSave";
import { useDraftOrderApprovalSend } from "../../hooks/useDraftOrderApprovalSend";
//
import { ShipmentOptionsPurchaseOrder } from "./ShipmentOptionsPurchaseOrder";
import { DownloadDocumentBtn } from "../../components/DownloadDocumentBtn";
// Utils
import { createOrderSaveObj } from "../../utils/Utils";
import { currencyFormatter } from "../../utils/Utils";

// MUI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
// import SendIcon from "@mui/icons-material/Send";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";

const stepsDefault = [
  {
    label: "Submitting Order One",
    description: `Please wait while we submit your order...`,
  },
  {
    label: "Submitting Order Two",
    description: "Submitting second order...",
  },
  {
    label: "Done",
    description: `Your orders have been successfully submitted.`,
  },
];

export function SubmitOrder({
  orderNo,
  poNo,
  setActiveStep,
  shipmentOne,
  shipmentTwo,
  user,
  orderFinaliseInfo,
  orderType = "draft",
}) {
  const twoShipments = shipmentTwo.lines.length > 0;
  const [submitStep, setSubmitStep] = useState(0);
  const [purchaseOrder, setPurchaseOrder] = useState(poNo);
  const [submit, setSubmit] = useState(false);
  const [submitApproval, setSubmitApproval] = useState(false);
  const [steps, setSteps] = useState(stepsDefault);

  // Check if any lines have over-entitlement reasons (from orderFinaliseInfo)
  const hasOverEntitlementItems =
    orderFinaliseInfo?.hasOverEntitlementItems || false;

  // Check if user is an Approver (can self-approve)
  const isApprover = user?.userType === "Approver";

  // Determine if escalation is required (Draft Order + Escalate mode + has over-entitlement items)
  // Approvers can self-approve, so they don't require escalation
  const requiresEscalation =
    orderType === "draft" &&
    user?.overEntitlementAction === "Escalate" &&
    hasOverEntitlementItems &&
    !isApprover;
  const navigate = useNavigate();
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const [isPOValid, setIsPOValid] = useState(true);

  // Delete step two if only one shipment
  const updateSteps = () => {
    if (!twoShipments) {
      setSteps([
        {
          label: "Submitting Order",
          description: `Please wait while we submit your order...`,
        },
        {
          label: "Done",
          description: `Your order has been successfully submitted.`,
        },
      ]);
    }
  };

  const {
    mutate: submitOrder,
    isSuccess: submitIsSuccess,
    data: saveData,
  } = useFinaliseOrderSave();

  // Success Function
  const submitSuccessFunc = () => {
    setSubmitStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Error Function
  const submitErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${saveData.data.status}: ${saveData.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    submitIsSuccess,
    saveData,
    saveData?.data?.status,
    submitSuccessFunc,
    submitErrorFunc,
  );

  // Finalise Order Save
  const handleSubmitClick = async () => {
    updateSteps();
    setSubmit(true);

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Wait one second before submitting the first order
    await delay(1000);
    submitOrder({
      data: createOrderSaveObj(
        orderNo,
        purchaseOrder,
        shipmentOne.date,
        shipmentOne.shipping.agent,
        shipmentOne.shipping.service,
        true,
        1,
        shipmentOne.lines,
      ),
    });

    if (twoShipments) {
      // Wait 5 seconds before placing the second order
      await delay(5000);
      submitOrder({
        data: createOrderSaveObj(
          orderNo,
          purchaseOrder,
          shipmentTwo.date,
          shipmentTwo.shipping.agent,
          shipmentTwo.shipping.service,
          true,
          2,
          shipmentTwo.lines,
        ),
      });
    }
  };

  // Approval
  const {
    mutate: submitOrderApproval,
    isSuccess: approvalIsSuccess,
    data: approvalData,
  } = useDraftOrderApprovalSend();

  // Success Function
  const approvalSuccessFunc = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSubmitApproval(true);
  };

  // Error Function
  const approvalErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${saveData.data.status}: ${saveData.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    approvalIsSuccess,
    approvalData,
    approvalData?.data?.status,
    approvalSuccessFunc,
    approvalErrorFunc,
  );

  const handleSumbitApprovalClick = () => {
    submitOrderApproval({ orderNo, poNo: purchaseOrder });
  };

  const handleFinish = () => {
    setSubmitStep((prevActiveStep) => prevActiveStep + 1);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // Redirect to Recieved Orders Page
    // setTimeout(() => {
    navigate({ to: "/received-orders" });
    // }, 3000);
  };

  const handleDone = () => {
    navigate({ to: "/" });
  };

  const canCheckout =
    user?.allowcheckout === true || user?.allowcheckout === "true";

  // If user can checkout but escalation is required, force approval path
  const showCheckoutButton = canCheckout && !requiresEscalation;
  const showApprovalButton = !canCheckout || requiresEscalation;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Stack spacing={2} sx={{ minWidth: 450, maxWidth: 600, p: 3 }}>
        <Paper sx={{ p: 2 }} elevation={3}>
          <Typography sx={{ fontWeight: "bold" }}>
            Total:{" "}
            {currencyFormatter("GB", shipmentOne.total + shipmentTwo.total)} ex.
            VAT
          </Typography>
        </Paper>
        {requiresEscalation && (
          <Paper sx={{ p: 2, bgcolor: "warning.light" }} elevation={1}>
            <Typography
              sx={{
                color: "warning.contrastText",
              }}
            >
              This order contains items that exceed the allowed entitlement and
              requires approval.
            </Typography>
          </Paper>
        )}
        {showCheckoutButton ? (
          <>
            <ShipmentOptionsPurchaseOrder
              purchaseOrder={purchaseOrder}
              purchaseOrderPattern={user.poNumberFormat}
              setPurchaseOrder={setPurchaseOrder}
              disabled={submit}
              isPOValid={isPOValid}
              setIsPOValid={setIsPOValid}
              required
            />

            <Button
              variant="contained"
              size="large"
              endIcon={<ShoppingCartCheckoutIcon />}
              disabled={submit || !purchaseOrder || !isPOValid}
              onClick={handleSubmitClick}
            >
              Checkout
            </Button>
          </>
        ) : (
          <Box>
            {!submitApproval && (
              <Stack spacing={2} sx={{ minWidth: 450, maxWidth: 600 }}>
                <ShipmentOptionsPurchaseOrder
                  purchaseOrder={purchaseOrder}
                  purchaseOrderPattern={user.poNumberFormat}
                  setPurchaseOrder={setPurchaseOrder}
                  disabled={submit}
                  isPOValid={isPOValid}
                  setIsPOValid={setIsPOValid}
                />
                <Button
                  // sx={{ mb: 1 }}
                  fullWidth
                  variant="contained"
                  size="large"
                  endIcon={<ShoppingCartCheckoutIcon />}
                  disabled={
                    submit ||
                    orderFinaliseInfo.awaitingApproval === "true" ||
                    !isPOValid
                  }
                  onClick={handleSumbitApprovalClick}
                >
                  Submit for approval
                </Button>
                {orderFinaliseInfo.awaitingApproval === "true" && (
                  <Typography>
                    This order has already been submitted for approval
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        )}

        {submitApproval && (
          <Box>
            <Typography>Your order has been submitted for approval.</Typography>
            {/* <Box sx={{ my: 2 }}> */}
            <Button
              variant="contained"
              onClick={handleDone}
              sx={{ my: 1 }}
              fullWidth
            >
              Return to Home
            </Button>
            {/* </Box> */}
          </Box>
        )}

        <DownloadDocumentBtn
          disabled={submit}
          docNo={orderNo}
          docType={"PDF"}
          path={"printdraftorder"}
        />

        {submit && (
          <Paper sx={{ p: 3 }}>
            {/* <Typography variant="h5" sx={{ pb: 3 }}>
          Submit Order
        </Typography> */}

            <Stepper activeStep={submitStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                    {step.label !== "Done" && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    )}
                    {step.label === "Done" && (
                      <Box sx={{ mb: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleFinish}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          View Orders
                        </Button>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
